import { useMemo } from 'react';

import {
	useStarterDashboardV2BreakpointState,
	useStarterDashboardV2PageData,
	useStarterDashboardV2TabsStateContext,
} from 'src/app/pages/starterDashboardV2/pageContext';

import type { ActionButtonProps } from '../../types';

export type UsePageHeaderReturn = ModelHookResult<
	{
		title: string;
		imageUrl?: string;
		actionButtons: Array<ActionButtonProps['actionType']>;
		mobile: boolean;
		orientation: 'horizontal' | 'vertical';
		buttonVariant: 'default' | 'square';
	},
	undefined
>;

export const SECONDARY_ACTION_BUTTONS = ['edit', 'view', 'link'] as const;

const useData = () => {
	const {
		data: {
			petition: { ask, photo },
			viewer: {
				experiments: {
					starterExperienceDashboardSunriseV1: { variation },
				},
			},
		},
	} = useStarterDashboardV2PageData();

	return { ask, photo, variation };
};

export function usePageHeader(): UsePageHeaderReturn {
	const { ask, photo, variation } = useData();
	const { isMobile } = useStarterDashboardV2BreakpointState();
	const imageUrl = useMemo(
		() =>
			photo?.petitionSmall.url && !photo.petitionSmall.url.includes('#processing')
				? photo.petitionSmall.url
				: undefined,
		[photo],
	);

	const shareButtonType = variation === 'variant2' ? 'shareExperimental' : 'share';

	const {
		data: { selectedTab },
	} = useStarterDashboardV2TabsStateContext();
	const actionButtons: Array<ActionButtonProps['actionType']> = useMemo(() => {
		if (!isMobile) {
			// Desktop: always show all buttons
			return [...SECONDARY_ACTION_BUTTONS, shareButtonType];
		}
		// Mobile
		if (selectedTab === 'home') {
			return [...SECONDARY_ACTION_BUTTONS, shareButtonType];
		}
		if (selectedTab === 'share') {
			return [shareButtonType];
		}
		return [];
	}, [isMobile, selectedTab, shareButtonType]);

	const orientation = actionButtons.length > 1 ? 'vertical' : 'horizontal';
	const buttonVariant = isMobile && actionButtons.length > 1 ? 'square' : 'default';

	return {
		data: {
			title: ask,
			imageUrl,
			actionButtons,
			mobile: isMobile,
			orientation,
			buttonVariant,
		},
	};
}
